/** Demo Mode only - aggregates cross-domain "needs your attention" items into DashboardV2's
 * existing Task shape (see ./types.ts) so they render inside the existing TasksCard/
 * UrgentAlertCard with no new UI. Sources: QuickOrder budget exceptions, Supplier Governance
 * approvals, Compliance action items, and Procurement Rollup rollout approvals.
 *
 * Additive only: returns an empty array outside Demo Mode, so production's real live task
 * feed (useDashboardOperationalData) is never altered by this. Each domain is still mocked
 * itself (no backend persistence), so these are representative illustrative items rather
 * than a live cross-domain query - consistent with how each source screen already presents
 * its own data. Each item is tagged with the persona whose approval-chain step it belongs to
 * and filtered by the demo Role Picker's selected persona, so switching roles only surfaces
 * the action relevant to that persona (no persona selected/"All" shows every step). */

import { useContext, useMemo } from 'react';
import { Task } from './types';
import { isDemoModeEnabled, DemoPersona } from '../../../utils/featureAccess';
import { UserContext } from '../../../components/shared/useUser';

interface PersonaTask extends Task {
  /** Persona whose approval-chain step this action item belongs to. Only shown when that
   * persona is selected in the demo Role Picker, or when no persona is selected ("All"). */
  persona: DemoPersona;
}

const ACTION_ITEMS: PersonaTask[] = [
  {
    id: 'action-item-budget-exception',
    title: 'Order over budget needs Head Office approval',
    dueDisplay: 'Needs approval',
    href: '/store/quick-order',
    priority: 90,
    taskType: 'user',
    persona: 'HeadOffice',
  },
  {
    id: 'action-item-supplier-governance',
    title: 'New supplier pending Procurement approval',
    dueDisplay: 'Needs approval',
    href: '/procurement/suppliers',
    priority: 85,
    taskType: 'user',
    persona: 'Procurement',
  },
  {
    id: 'action-item-compliance',
    title: 'Compliance action items need review',
    dueDisplay: 'Review required',
    href: '/compliance/audits/action-items',
    priority: 80,
    taskType: 'user',
    persona: 'Compliance',
  },
  {
    id: 'action-item-rollout-approval',
    title: 'Product trial ready for rollout approval',
    dueDisplay: 'Needs approval',
    href: '/range-planning/offer-overview',
    priority: 75,
    taskType: 'user',
    persona: 'Operations',
  },
  {
    id: 'action-item-margin-approval',
    title: 'Recipe margin change needs Commercial approval',
    dueDisplay: 'Needs approval',
    href: '/store/recipes-v2',
    priority: 85,
    taskType: 'user',
    persona: 'Commercial',
  },
  {
    id: 'action-item-recipe-ai-check',
    title: 'AI recipe check flagged an issue for review',
    dueDisplay: 'Review required',
    href: '/store/recipes-v2',
    priority: 70,
    taskType: 'user',
    persona: 'Chef',
  },
];

export function useDashboardActionItems(): Task[] {
  const isDemoMode = isDemoModeEnabled();
  const { demoPersona } = useContext(UserContext);

  return useMemo(() => {
    if (!isDemoMode) return [];

    // Role Picker only ever narrows what's shown - no persona selected ("All") surfaces every
    // approval-chain step, matching hasPermissionTo's narrowing behaviour elsewhere.
    return ACTION_ITEMS
      .filter((item) => !demoPersona || item.persona === demoPersona)
      .map(({ persona: _persona, ...task }) => task);
  }, [isDemoMode, demoPersona]);
}
